import { TextField, type TextFieldProps, inputBaseClasses, inputLabelClasses, formHelperTextClasses } from '@mui/material';
import { merge } from 'lodash';
import { FontNormalSxProps } from '../../../_constants';

declare module '@mui/material' {
  export interface BaseTextFieldProps {
    'data-qa'?: string;
  }
}

export interface AoaInputProps extends Omit<TextFieldProps, 'variant'> {
  /**
   * Data-qa tag for E2E test purposes
   */
  readonly 'data-qa'?: string;
  /**
   * Whether this input field is optional
   */
  readonly optional?: boolean;
}

/**
 * Constructs an input field using pre-defined Rijks styling
 *
 * @param props - Props to pass to the input field
 * @example
 * ```jsx
 * <AoaInput label="User name" value={value} onChange={() => handleOnChange} />
 * ```
 */
export function AoaInput({ optional, ...props }: AoaInputProps) {
  return (
    <TextField
      {...props}
      required={!optional}
      slotProps={{
        inputLabel: { shrink: false, ...props.slotProps?.inputLabel },
        ...props.slotProps
      }}
      sx={merge(
        {
          [`.${inputBaseClasses.root}`]: {
            ...FontNormalSxProps,
            marginTop: '35px',

            [`&.${inputBaseClasses.multiline}`]: {
              padding: 0
            },

            [`:not(.${formHelperTextClasses.error})::after`]: {
              borderColor: 'var(--color-rijks-skyblue)'
            },

            [`.${inputBaseClasses.input}`]: {
              padding: '17px 12px 16px',

              ':disabled': {
                backgroundColor: 'var(--color-disabled)',
                borderColor: 'var(--color-disabled)'
              }
            }
          },

          [`.${inputLabelClasses.root}`]: {
            ...FontNormalSxProps,
            marginLeft: '-14px',
            marginTop: '-10px',

            ':disabled': {
              color: 'var(--color-text-disabled)'
            },

            [`:focus:not(.${formHelperTextClasses.error})`]: {
              color: 'var(--color-rijks-skyblue)'
            },

            [`:not(.${formHelperTextClasses.required})::after`]: {
              content: '"(optioneel)"',
              marginLeft: '5px',
              fontSize: 'calc(var(--font-size-text) * 0.75)'
            },

            [`&.${formHelperTextClasses.error}`]: {
              ...FontNormalSxProps,
              fontWeight: 'bold'
            },

            [`&.${formHelperTextClasses.disabled}`]: {
              color: 'var(--color-text-disabled)'
            }
          }
        },
        props.sx
      )}
      variant='outlined'
    />
  );
}
